import React from 'react'
import { Icon, IconChoices } from '../common/Icon'
import { theme } from '../../theme/theme'
import { useAppTranslation } from '../../hooks/useAppTranslation'

export type TodoItemProps = {
  title: string
  description: string
  done: boolean
  isLoading?: boolean
  icon: IconChoices
}

const Item = ({
  title,
  description,
  done,
  isLoading,
  icon,
  index,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}: TodoItemProps & { index: number }) => {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className="flex flex-row items-baseline"
      style={{
        paddingTop: theme.spacing.m,
        paddingBottom: theme.spacing.m,
        borderBottomColor: theme.colors.lightGray,
        borderBottomWidth: 1,
      }}
    >
      {done ? (
        <Icon icon={IconChoices.CHECK_CIRCLE} highlighted />
      ) : (
        <div
          className="border-neutral-80"
          style={{
            width: 24,
            height: 24,
            borderRadius: 12,
            backgroundColor: theme.colors.white,
            borderWidth: 1,
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <div style={theme.textVariants.base}>{index + 1}</div>
        </div>
      )}
      <div className="flex-1 mx-2">
        <div className="flex flex-row items-center">
          <Icon icon={icon} />
          <p className="ml-2" style={theme.textVariants.baseBold}>
            {title}
          </p>
        </div>
        <p style={theme.textVariants.base}>{description}</p>
      </div>
      {isLoading ? (
        <div
          className={`h-[24px] w-[24px] animate-spin 
        rounded-full border-2 border-solid border-t-transparent border-black`}
        />
      ) : (
        <Icon size={12} icon={IconChoices.CHEVRON_RIGHT} />
      )}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const ProgressBar = ({ percent }: { percent: number }) => {
  return (
    <div
      style={{
        height: 8,
        backgroundColor: theme.colors.lightGray,
        borderRadius: 4,
      }}
    >
      <div
        style={{
          height: 8,
          backgroundColor: theme.colors.brandGreen,
          borderRadius: 4,
          width: `${percent}%`,
        }}
      />
    </div>
  )
}

const ProgressText = ({
  percent,
  stepsDone,
  totalSteps,
}: {
  percent: number
  totalSteps: number
  stepsDone: number
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}) => {
  const { translate } = useAppTranslation()
  return (
    <div className="flex flex-row justify-between">
      <div style={theme.textVariants.base}>
        {translate('getToKnowScreen.progress', {
          completed: stepsDone,
          total: totalSteps,
        })}
      </div>
      <div className="text-brandGreen" style={{ ...theme.textVariants.base }}>
        {percent}%
      </div>
    </div>
  )
}

const Progress = ({
  stepsDone,
  totalSteps,
}: {
  totalSteps: number
  stepsDone: number
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}) => {
  const percent = Math.round((stepsDone / totalSteps) * 100)
  return (
    <div>
      <ProgressText
        percent={percent}
        stepsDone={stepsDone}
        totalSteps={totalSteps}
      />
      <ProgressBar percent={percent} />
    </div>
  )
}

const Todo = {
  Item,
  Progress,
}

export { Todo }
